<template>
  <div class="flsa-review-content w-100">
    <div class="flsa-header-block px-8 pt-8 pb-4">
      <div class="d-flex flex-column ga-1">
        <div class="d-flex">
          <JobProfileStatus
            :is-template="isTemplate"
            :is-hris-profile="isHrisProfile"
            :status="jobProfile?.status"
          />
        </div>

        <div class="text-h1 text-textDarkest">
          {{ jobProfile?.title || 'Job Profile' }}
        </div>

        <JobProfileMeta :profile="jobProfile" />
      </div>
    </div>

    <div class="flsa-body px-8 pb-8 pt-4">
      <div class="flsa-content-rail">
        <div v-if="currentSectionId === 'summary'" class="flsa-summary">
          <div class="text-h1 text-textDarkest mb-1">
            Summary
          </div>
          <div class="text-body-1 text-textLight mb-4">
            <template v-if="readonly">
              Review submitted responses and determination.
            </template>
            <template v-else>
              Review responses, record the final determination, and submit.
            </template>
          </div>

          <div class="flsa-summary-list mb-6">
            <FlsaSummaryRow
              v-for="row in summaryRows"
              :key="row.id"
              :label="row.label"
              :checked-count="row.checkedCount"
              :total-count="row.totalCount"
              :questions="row.questions"
              :section-id="row.id"
              :answers="answers"
              :readonly="readonly"
              @update:answer="({ questionId, value }) => setAnswer(row.id, questionId, value)"
            />
          </div>

          <div class="text-h3 text-textDarkest mb-2">
            Final determination
          </div>
          <div
            v-if="readonly"
            class="d-flex flex-column ga-1 flsa-readonly-value mb-4"
          >
            <div class="text-body-1 text-textDarkest">
              {{ determinationDisplay }}
            </div>
            <div
              v-if="determinationSubtextDisplay"
              class="text-body-2 text-textLight"
            >
              {{ determinationSubtextDisplay }}
            </div>
          </div>
          <div
            v-else
            class="flsa-determination-cards mb-4"
          >
            <FlsaDeterminationCard
              label="Exempt"
              sublabel="Role is determined to be exempt from FLSA"
              :selected="determination === 'exempt'"
              @click="handleDeterminationSelect('exempt')"
            />
            <FlsaDeterminationCard
              label="Non-exempt"
              sublabel="Role is determined to be non-exempt from FLSA"
              :selected="determination === 'non-exempt'"
              @click="handleDeterminationSelect('non-exempt')"
            />
          </div>
          <template v-if="determination === 'exempt'">
            <template v-if="readonly">
              <div class="text-h3 text-textDarkest mb-2">
                Primary exemption basis
              </div>
              <div
                class="text-body-1 text-textDarkest flsa-readonly-value mb-4"
              >
                {{ primaryExemptionBasisDisplay }}
              </div>
            </template>
            <div
              v-else
              class="flsa-determination-exemption-basis mb-4"
            >
              <div class="text-body-2 text-textDark">
                Primary exemption basis
              </div>
              <v-select
                v-model="primaryExemptionBasis"
                :items="exemptionBasisItems"
                item-title="title"
                item-value="value"
                placeholder="Exemption category"
                color="primary"
                hide-details
                density="comfortable"
                variant="outlined"
                :menu-props="{ contentClass: 'flsa-determination-select-menu' }"
                class="flsa-determination-select"
              />
            </div>
          </template>

          <div class="text-h3 text-textDarkest mb-2">
            Notes
          </div>
          <v-textarea
            v-if="!readonly"
            v-model="notesText"
            placeholder="Add any context that was used to make the final determination"
            color="primary"
            variant="outlined"
            hide-details
            rows="3"
            class="flsa-notes-textarea mb-4"
          />
          <div
            v-else
            class="text-body-1 text-textDarkest flsa-readonly-value mb-4"
          >
            {{ notesDisplay }}
          </div>

          <DpplNewButton
            v-if="!readonly"
            color="primary"
            variant="flat"
            class="text-button-2 mb-4"
            :disabled="!determination || isSubmitting"
            :loading="isSubmitting"
            @click="handleSubmit"
          >
            Submit FLSA Form
          </DpplNewButton>
          <div
            v-else
            class="text-body-2 text-textLight mb-4"
          >
            Submitted {{ formattedSubmittedAt }}
          </div>

          <div class="flsa-note text-body-3 text-textLight">
            This record supports an FLSA classification determination and does not constitute
            legal advice. See
            <a
              class="flsa-note-link"
              :href="eligibilityCitation.cfr.url"
              target="_blank"
              rel="noopener noreferrer"
            >DOL Fact Sheet #{{ eligibilityCitation.fact_sheet.ref }} — {{ eligibilityCitation.cfr.ref }}</a>.
          </div>
        </div>

        <div
          v-else-if="currentSection"
          class="flsa-section"
        >
          <div class="text-h1 text-textDarkest mb-1">
            {{ currentSection.title }}
          </div>
          <div class="text-body-1 text-textLight mb-4">
            {{ currentSection.description }}
            See
            <a
              class="flsa-note-link"
              :href="currentSection.citation.fact_sheet.url"
              target="_blank"
              rel="noopener noreferrer"
            >DOL Fact Sheet #{{ currentSection.citation.fact_sheet.ref }}</a>
            and
            <a
              class="flsa-note-link"
              :href="currentSection.citation.cfr.url"
              target="_blank"
              rel="noopener noreferrer"
            >{{ currentSection.citation.cfr.ref }}</a>.
          </div>

          <div
            v-for="(group, groupIndex) in currentSection.groups"
            :key="group.label ?? `group-${groupIndex}`"
            class="mb-4"
          >
            <div
              v-if="group.label"
              class="text-h3 text-textDarkest mb-3"
            >
              {{ group.label }}
            </div>
            <FlsaQuestionRow
              v-for="question in group.questions"
              :key="question.id"
              :question-id="question.id"
              :question="question.question"
              :hint="question.hint"
              :citations="question.citations"
              :model-value="answerFor(currentSectionId, question.id)"
              :readonly="readonly"
              @update:model-value="setAnswer(currentSectionId, question.id, $event)"
            />
          </div>

          <div
            v-if="!readonly"
            class="d-flex justify-end mt-4"
          >
            <DpplNewButton
              color="primary"
              variant="flat"
              class="text-button-2"
              @click="emit('update:section', nextSectionId)"
            >
              Next
            </DpplNewButton>
          </div>
        </div>

        <div
          v-else
          class="flsa-empty-section"
        >
          <div class="text-h1 text-textDarkest mb-2">
            {{ sectionLabel(currentSectionId) }}
          </div>
          <div class="text-body-1 text-textLight">
            This section is not available.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import { FLSA_SECTIONS } from '@/helpers/constants/flsaQuestions';
import { formStateToDataJson, submissionToFormState } from '@/helpers/flsa';
import { formatDisplayDate } from '@/helpers/date';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import FlsaDeterminationCard from './FlsaDeterminationCard.vue';
import FlsaQuestionRow from './FlsaQuestionRow.vue';
import FlsaSummaryRow from './FlsaSummaryRow.vue';
import JobProfileMeta from './JobProfileMeta.vue';
import JobProfileStatus from './JobProfileStatus.vue';

const VALID_SECTION_IDS = new Set([
  'eligibility',
  ...FLSA_SECTIONS.map((s) => s.id),
  'summary',
]);

const props = defineProps({
  section: {
    type: String,
    default: 'summary',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  submittedAt: {
    type: String,
    default: null,
  },
  initialData: {
    type: Object,
    default: null,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:section', 'submit']);

const { jobProfile, isTemplate, isHrisProfile } = storeToRefs(useCurrentJobProfile());

const determination = ref(null);
const answers = ref({});
const primaryExemptionBasis = ref(null);
const notesText = ref('');

watch(() => props.initialData, (data) => {
  const {
    determination: determinationFromSubmission,
    primaryExemptionBasis: primaryExemptionBasisFromSubmission,
    notesText: notesTextFromSubmission,
    answers: answersFromSubmission,
  } = submissionToFormState(data ?? null);

  determination.value = determinationFromSubmission;
  primaryExemptionBasis.value = primaryExemptionBasisFromSubmission;
  notesText.value = notesTextFromSubmission;
  answers.value = answersFromSubmission;
}, { immediate: true });

const exemptionBasisItems = computed(() => (
  FLSA_SECTIONS
    .filter((s) => s.isExemption)
    .map((s) => ({ title: s.title, value: s.id }))
));

const formattedSubmittedAt = computed(() => formatDisplayDate(props.submittedAt));

const determinationDisplay = computed(() => {
  if (determination.value === 'exempt') return 'Exempt';
  if (determination.value === 'non-exempt') return 'Non-exempt';
  return '—';
});

const determinationSubtextDisplay = computed(() => {
  if (determination.value === 'exempt') return 'Role is determined to be exempt from FLSA';
  if (determination.value === 'non-exempt') return 'Role is determined to be non-exempt from FLSA';
  return '';
});

const primaryExemptionBasisDisplay = computed(() => {
  const match = FLSA_SECTIONS.find((s) => s.id === primaryExemptionBasis.value);
  return match?.title ?? '—';
});

const notesDisplay = computed(() => (
  (notesText.value && String(notesText.value).trim()) ? notesText.value : '—'
));

const eligibilityCitation = computed(() => (
  FLSA_SECTIONS.find((section) => section.id === 'eligibility')?.citation ?? null
));

const currentSectionId = computed(() => (
  VALID_SECTION_IDS.has(props.section) ? props.section : 'summary'
));

const currentSection = computed(() => (
  FLSA_SECTIONS.find((section) => section.id === currentSectionId.value) ?? null
));

const nextSectionId = computed(() => {
  const idx = FLSA_SECTIONS.findIndex((section) => section.id === currentSectionId.value);

  if (idx === -1 || idx === FLSA_SECTIONS.length - 1) {
    return 'summary';
  }

  return FLSA_SECTIONS[idx + 1].id;
});

const answerKey = (sectionId, questionId) => `${sectionId}:${questionId}`;

const answerFor = (sectionId, questionId) => (
  answers.value[answerKey(sectionId, questionId)] ?? false
);

const setAnswer = (sectionId, questionId, value) => {
  if (props.readonly) return;

  const key = answerKey(sectionId, questionId);

  answers.value = { ...answers.value, [key]: value };
};

const summaryRows = computed(() => FLSA_SECTIONS.map((section) => {
  const allQuestions = section.groups.flatMap((group) => group.questions);
  const checkedCount = allQuestions.filter((question) => answerFor(section.id, question.id)).length;

  return {
    id: section.id,
    label: section.title,
    checkedCount,
    totalCount: allQuestions.length,
    questions: allQuestions,
  };
}));

const handleDeterminationSelect = (value) => {
  if (props.readonly) return;

  determination.value = determination.value === value ? null : value;

  if (determination.value !== 'exempt') {
    primaryExemptionBasis.value = null;
  }
};

const handleSubmit = () => {
  emit('submit', formStateToDataJson({
    determination: determination.value,
    primaryExemptionBasis: primaryExemptionBasis.value,
    notesText: notesText.value,
    answers: answers.value,
  }));
};

const sectionLabel = (sectionId) => FLSA_SECTIONS.find((section) => section.id === sectionId)?.title ?? 'Section';
</script>

<style scoped lang="less">
.flsa-header-block {
  border-bottom: 1px solid @stroke-default;
}

.flsa-content-rail {
  max-width: 784px;
}

.flsa-summary-list {
  border-top: 1px solid @stroke-default;
}

.flsa-determination-cards {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr 1fr;
}

.flsa-determination-exemption-basis {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.flsa-readonly-value {
  padding-top: 4px;
}

.flsa-empty-section {
  border: 1px dashed @stroke-default;
  border-radius: 8px;
  padding: 24px;
}

.flsa-note {
  background: @bg-canvas;
  border: 1px solid @stroke-default;
  border-radius: 8px;
  padding: 12px;
}

.flsa-determination-select {
  width: 268px;
  max-width: 100%;

  :deep(.v-field__append-inner .v-icon) {
    color: @text-dark;
  }
}

.flsa-determination-select,
.flsa-notes-textarea {
  :deep(.v-field) {
    font-size: 14px;
    color: @text-darkest;
  }

  :deep(.v-select__selection--placeholder),
  :deep(textarea::placeholder) {
    color: @text-dark;
  }
}

.flsa-note-link {
  color: @link-default;
  text-decoration: underline;

  &:hover {
    color: @link-hover;
  }
}
</style>

<style lang="less">
.flsa-determination-select-menu.v-overlay__content {
  width: 268px;
  max-width: 100%;
}

.flsa-determination-select-menu.v-overlay__content > .v-list {
  box-shadow: none;
  border: 1px solid @stroke-default;
  border-radius: 4px;
  padding: 0;
  background-color: @bg-default;
  width: 100%;
  min-width: 0;
}

.flsa-determination-select-menu .v-list-item {
  padding: 8px 12px;
  min-height: unset;
}

.flsa-determination-select-menu .v-list-item-title {
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  color: @text-dark;
}
</style>
